import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type {
  GetApiV1ProjectsProjectIdPanelSheetsPanelSheetIdPositionParams,
  PanelImportApiDTO,
  PanelSheetStatusApiDTO,
  PollsterApiDTO,
  ProjectApiDTO,
} from './../codegen/generated/Api.schemas'
import type { PanelSheetFilter } from './../types/Panel.type'

type AllFiltersType = {
  panelImportId?: PanelImportApiDTO['id']
  status?: PanelSheetStatusApiDTO
  handedTo?: PollsterApiDTO['id'] | undefined
  dynamicFilters?: PanelSheetFilter[]
}

type PanelFilterStoreType = {
  filters: Record<ProjectApiDTO['id'], AllFiltersType>
  setFilter: (projectId: ProjectApiDTO['id'], filter: Partial<AllFiltersType>) => void
  getApiStaticFilters: (
    projectId: ProjectApiDTO['id'],
  ) => GetApiV1ProjectsProjectIdPanelSheetsPanelSheetIdPositionParams
  getApiFilters: (
    projectId: ProjectApiDTO['id'],
  ) => GetApiV1ProjectsProjectIdPanelSheetsPanelSheetIdPositionParams
  reset: () => void
}

const KEY = 'tsa-panel-filter'

export const usePanelFilterStore = create(
  persist<PanelFilterStoreType>(
    (set, get, store) => ({
      filters: {},
      setFilter: (projectId, filter) => {
        const { filters } = get()
        const currentFilter = filters[projectId]

        set({
          filters: {
            ...get().filters,
            ...{
              [projectId]: {
                ...currentFilter,
                ...filter,
              },
            },
          },
        })
      },
      getApiStaticFilters: projectId => {
        const { filters } = get()
        const pf = filters[projectId]

        if (pf) {
          const { panelImportId, status, handedTo } = pf

          return {
            ...(panelImportId ? { panelImportIds: [panelImportId] } : undefined),
            ...(status ? { status } : undefined),
            ...(handedTo ? { handedTo } : undefined),
          }
        }

        return {}
      },
      getApiFilters: projectId => {
        const { filters, getApiStaticFilters } = get()
        const pf = filters[projectId]

        if (pf) {
          const { dynamicFilters } = pf
          const staticFilters = getApiStaticFilters(projectId)
          const filteredDynamicFilters = dynamicFilters?.filter(
            df =>
              df.subject !== '' &&
              df.predicate !== '' &&
              (!Array.isArray(df.predicate) || df.predicate.length > 0),
          )
          return {
            ...staticFilters,
            ...(filteredDynamicFilters
              ? { outputData: JSON.stringify(filteredDynamicFilters) }
              : undefined),
          }
        }

        return {}
      },
      reset: () => {
        localStorage.removeItem(KEY)
        set(() => store.getInitialState())
      },
    }),
    { name: KEY, storage: createJSONStorage(() => localStorage) },
  ),
)
